import { SELLING_PLAN_GROUP_ADDON_TAG, SELLING_PLAN_GROUP_HIDDEN_TAG } from "@smartrr/shared/constants";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { filterBundleParentsFromPurchasables, getBundleParentFromLineItems } from "@smartrr/shared/utils/bundles";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { isVariantInStock } from "@smartrr/shared/utils/variants";
import flatten from 'lodash/flatten';

import {
  useProductsListForSellingPlanId,
  useSellingPlanGroups,
} from "@customer-app/state/reducers/sellingPlanGroups";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { useCurrentBundle } from "../../AquariusInner/hooks/useCurrentBundle";

export const usePurchaseablesForAdding = (
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState> | undefined
) => {
  const { bundle, allBundles } = useCurrentBundle(delivery);

  const sellingPlanGroupsByProductId = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByProductId
  );
  const sellingPlanGroupsByVariantId = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByVariantId
  );

  const sellingPlanGroups = useSellingPlanGroups();

  const bundleParentItem = getBundleParentFromLineItems(delivery?.purchaseState.stLineItems ?? []);

  const addonSellingPlanId = sellingPlanGroups.find(
    sellingPlanGroup =>
      sellingPlanGroup.name.includes(SELLING_PLAN_GROUP_ADDON_TAG) &&
      sellingPlanGroup.name.includes(SELLING_PLAN_GROUP_HIDDEN_TAG)
  )?.shopifyNumericId;

  return useProductsListForSellingPlanId(addonSellingPlanId).filter(purchasable => {
    if (
      !purchasable.shopifyId ||
      !purchasable.isActiveInShopify ||
      !purchasable.vnts?.filter(vnt => vnt.isActiveInShopify).length ||
      purchasable.isDraftOrArchived
    ) {
      return false;
    }

    if (filterBundleParentsFromPurchasables(allBundles, purchasable)) {
      return false;
    }

    if (bundle && bundleParentItem && purchasable.vnts.filter(vnt => vnt.id === bundleParentItem.vnt.id).length) {
      return false;
    }

    const areAnyVariantsInStock = purchasable.vnts?.find(vnt => isVariantInStock(vnt));
    if (!areAnyVariantsInStock) {
      return false;
    }

    const productSellingPlanGroups = sellingPlanGroupsByProductId[shopifyGidToNumber(purchasable.shopifyId)];
    const variantSellingPlanGroups = flatten(
      purchasable.vnts?.map(vnt =>
        vnt.shopifyId ? sellingPlanGroupsByVariantId[shopifyGidToNumber(vnt.shopifyId)] : undefined
      )
    );
    return (
      productSellingPlanGroups?.some(g => Boolean(g.sellingPlans.length)) ||
      variantSellingPlanGroups.some(g => g && Boolean(g.sellingPlans.length))
    );
  });
};
